import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import { APP_NAME, ROUTES } from '@utils/constants';

const TermsOfServicePage: React.FC = () => {
  const lastUpdated = 'December 14, 2025';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to={ROUTES.HOME}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Page header */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600">
              Last updated: {lastUpdated}
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-8 prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Welcome to {APP_NAME}. These Terms of Service ("Terms") govern your access to and use
                of our platform, website, and services (collectively, the "Service"). By accessing or
                using {APP_NAME}, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-700 mb-4">
                {APP_NAME} is a multi-tenant SaaS platform that enables professional photographers
                ("Photographers") to showcase event photos and sell digital downloads to their clients
                ("Customers"). The platform facilitates transactions between Photographers and Customers
                while providing hosting, payment processing, and marketing tools.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. User Types and Account Registration</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.1 Photographers (B2B Users)</h3>
              <p className="text-gray-700 mb-4">
                Photographers must register for a subscription account to use {APP_NAME}'s services.
                By registering, Photographers agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of their account credentials</li>
                <li>Accept responsibility for all activities under their account</li>
                <li>Complete identity verification as required by our payment processor (Stripe)</li>
                <li>Comply with all applicable laws regarding their photography business</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.2 Customers (B2C Users)</h3>
              <p className="text-gray-700 mb-4">
                Customers may browse and purchase photos through Photographer galleries. Customers agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide accurate payment and contact information</li>
                <li>Use purchased photos only for personal, non-commercial purposes unless otherwise agreed</li>
                <li>Not redistribute or resell purchased photos</li>
                <li>Respect the intellectual property rights of Photographers</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Subscription Terms</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Subscription Plans</h3>
              <p className="text-gray-700 mb-4">
                {APP_NAME} offers the following subscription tiers for Photographers:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Basic Plan:</strong> $17.99/month or $179.00/year - 10GB storage, 5 events</li>
                <li><strong>Pro Plan:</strong> $49.99/month or $497.90/year - 100GB storage, 25 events</li>
                <li><strong>Enterprise Plan:</strong> $149.99/month or $1,493.90/year - 1TB storage, unlimited events</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.2 Free Trial</h3>
              <p className="text-gray-700 mb-4">
                New Photographers receive a 14-day free trial with full feature access. A valid payment
                method is required to start the trial. At the end of the trial, your subscription will
                automatically convert to the Basic plan unless you select a different plan or cancel.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.3 Billing and Payment</h3>
              <p className="text-gray-700 mb-4">
                Subscriptions are billed in advance on a monthly or annual basis. All payments are
                processed securely through Stripe. You authorize us to charge your payment method for
                all fees incurred under your account.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.4 Cancellation</h3>
              <p className="text-gray-700 mb-4">
                You may cancel your subscription at any time. Upon cancellation:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>You retain access until the end of your current billing period</li>
                <li>Your data will be retained for 30 days post-cancellation</li>
                <li>After 30 days, your data may be permanently deleted</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Photo Sales and Payments</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">4.1 Multi-Party Payment Structure</h3>
              <p className="text-gray-700 mb-4">
                {APP_NAME} facilitates payments between Customers and Photographers using Stripe Connect.
                When a Customer purchases photos:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>{APP_NAME} collects a 3% platform fee on all photo sales</li>
                <li>Stripe collects standard payment processing fees (approximately 2.9% + $0.30)</li>
                <li>The remaining amount is transferred to the Photographer's connected Stripe account</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">4.2 Pricing</h3>
              <p className="text-gray-700 mb-4">
                Photographers set their own prices for photos within their galleries. {APP_NAME} provides
                suggested pricing guidelines but does not mandate specific pricing.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">4.3 Payouts</h3>
              <p className="text-gray-700 mb-4">
                Photographer payouts are processed according to the Stripe Connect payout schedule,
                typically within 2-7 business days of the transaction. Photographers are responsible
                for any applicable taxes on their earnings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">5.1 Photographer Content</h3>
              <p className="text-gray-700 mb-4">
                Photographers retain full ownership of all photos uploaded to {APP_NAME}. By uploading
                content, Photographers grant {APP_NAME} a limited license to display, distribute, and
                process the photos solely for the purpose of operating the Service.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">5.2 Customer License</h3>
              <p className="text-gray-700 mb-4">
                Upon purchasing photos, Customers receive a personal, non-exclusive, non-transferable
                license to use the photos for personal purposes. Commercial use requires a separate
                agreement with the Photographer.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">5.3 Platform Content</h3>
              <p className="text-gray-700 mb-4">
                All {APP_NAME} branding, logos, designs, and platform features are the property of
                {APP_NAME} and may not be used without prior written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">
                Users agree not to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Upload or share illegal, harmful, or offensive content</li>
                <li>Violate intellectual property rights of others</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Use the Service for fraudulent or deceptive purposes</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Harvest or collect user data without permission</li>
                <li>Use automated systems to access the Service without authorization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Refunds</h2>
              <p className="text-gray-700 mb-4">
                Please refer to our <Link to={ROUTES.REFUND_POLICY} className="text-primary-600 hover:text-primary-700">Refund Policy</Link> for
                detailed information about refunds for subscriptions and photo purchases.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Privacy</h2>
              <p className="text-gray-700 mb-4">
                Your use of {APP_NAME} is subject to our <Link to={ROUTES.PRIVACY_POLICY} className="text-primary-600 hover:text-primary-700">Privacy Policy</Link>,
                which describes how we collect, use, and protect your personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers and Limitations</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">9.1 Service Availability</h3>
              <p className="text-gray-700 mb-4">
                {APP_NAME} is provided "as is" without warranties of any kind. We do not guarantee
                uninterrupted, secure, or error-free operation of the Service.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">9.2 Limitation of Liability</h3>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, {APP_NAME} shall not be liable for any indirect,
                incidental, special, consequential, or punitive damages, including loss of profits, data,
                or business opportunities.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">9.3 Third-Party Services</h3>
              <p className="text-gray-700 mb-4">
                {APP_NAME} integrates with third-party services including Stripe for payments and AWS
                for hosting. We are not responsible for the availability or performance of these
                third-party services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-700 mb-4">
                {APP_NAME} reserves the right to suspend or terminate accounts that violate these Terms
                or engage in fraudulent, abusive, or illegal activity. Upon termination:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Access to the Service will be immediately revoked</li>
                <li>Outstanding payments remain due</li>
                <li>Data may be deleted according to our data retention policies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We may update these Terms from time to time. We will notify users of material changes
                via email or through the Service. Continued use of the Service after changes take effect
                constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of the State of California, United States, without
                regard to conflict of law principles. Any disputes arising from these Terms shall be
                resolved in the courts of San Francisco County, California.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              <ul className="list-none text-gray-700">
                <li><strong>Email:</strong> legal@n3rd-media.com</li>
                <li><strong>Address:</strong> Pixel Share, San Francisco, CA</li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
